import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ArrowRight, Check, Hash, Plus, PhoneCall } from 'lucide-react';
import { PageHeader, Button, Badge, Card, EmptyState } from '@/shared/ui';
import { formatPhoneDisplay } from '@/shared/utils/validation';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './AgentsPage.module.css';
import { AgentBuilder } from '../components/AgentBuilder';
import { ClosuresPanel } from '../components/ClosuresPanel';
import { LoadError } from '../components/LoadError';
import { activatableProps } from '../utils/a11y';
import { useCallAgents } from '../hooks/useCallAgents';
import { useAgentHealth } from '../hooks/useAgentHealth';
import { useCanManageCalls } from '../hooks/useCanManageCalls';
import { useCallAgentActions } from '../hooks/useCallAgentActions';
import { useCallAvailableNumbers } from '../hooks/useCallAvailableNumbers';
import {
  TEMPLATES,
  resolveTemplate,
  makeDraft,
  draftToInput,
  areaCodeFromPhone,
} from '../agentShared';
import type {
  AutoActivateState,
  CallTemplate,
  DraftAgent,
  Prefill,
  TemplateKey,
} from '../agentShared';
import { locationsApi } from '@/modules/settings/api/locationsApi';
import type { LocationProfile, DayHours } from '@/modules/settings/api/locationsApi';
import { settingsApi } from '@/modules/settings/api/settingsApi';
import { settingsKeys } from '@/modules/settings/queryKeys';
import { useLocation } from '@/shared/hooks/useLocation';
import { callsApi } from '../api/callsApi';
import type { AgentHealthIssue, CallAgent } from '../api/callsApi';
import { callsKeys } from '../queryKeys';

// ── Office hours + address formatters ─────────────────────────────────────────

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function fmt12(time: string): string {
  const [hStr, mStr] = time.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const suffix = h < 12 ? 'am' : 'pm';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}${suffix}` : `${h12}:${mStr}${suffix}`;
}

function formatOfficeHours(officeHours: Record<string, DayHours | null> | null): string {
  if (!officeHours) return '';

  // Build a label for each day: "8am–5pm" or null (closed)
  const labels: (string | null)[] = DAY_KEYS.map((d) => {
    const slot = officeHours[d];
    if (!slot) return null;
    return `${fmt12(slot.open)}–${fmt12(slot.close)}`;
  });

  // Group consecutive days with the same label
  const groups: { label: string; start: number; end: number }[] = [];
  for (let i = 0; i < labels.length; i++) {
    const label = labels[i];
    if (!label) continue;
    const last = groups[groups.length - 1];
    // Only extend a run when this day immediately follows the previous one —
    // a closed day in between must break the run, even if the hours on
    // either side happen to match (e.g. closed Thursdays).
    if (last && last.label === label && last.end === i - 1) {
      last.end = i;
    } else {
      groups.push({ label, start: i, end: i });
    }
  }

  if (!groups.length) return '';
  return groups
    .map(({ label, start, end }) =>
      start === end
        ? `${DAY_SHORT[start]} ${label}`
        : `${DAY_SHORT[start]}–${DAY_SHORT[end]} ${label}`,
    )
    .join(', ');
}

function formatAddress(
  location: { address?: string; city?: string; state?: string } | null,
): string {
  if (!location) return '';
  const parts = [location.address, location.city, location.state].filter(Boolean);
  return parts.join(', ');
}

function agentStatusBadge(status: string) {
  if (status === 'active')
    return (
      <Badge variant="success">
        <Check size={11} /> Active
      </Badge>
    );
  if (status === 'paused') return <Badge variant="warning">Paused</Badge>;
  return <Badge variant="neutral">Draft</Badge>;
}

// A template's live instance count, treating legacy 'overflow' drafts as Assist.
function instancesOf(template: CallTemplate, agents: CallAgent[]): CallAgent[] {
  return agents.filter(
    (a) => (a.templateKey === 'overflow' ? 'assist' : a.templateKey) === template.id,
  );
}

// ── Template gallery ──────────────────────────────────────────────────────────

function templateBadge(t: CallTemplate) {
  if (t.badge === 'new')
    return (
      <Badge variant="success" size="sm">
        New
      </Badge>
    );
  if (t.badge === 'specialists')
    return (
      <Badge variant="info" size="sm">
        For specialists
      </Badge>
    );
  return null;
}

function Gallery({
  agents,
  specialty,
  onSelect,
  onOpenAgent,
  onBack,
}: {
  agents: CallAgent[];
  specialty: string | null;
  onSelect: (id: TemplateKey) => void;
  onOpenAgent: (a: CallAgent) => void;
  onBack: () => void;
}) {
  // Specialist templates float to the top for matching practice types
  // (still available to everyone).
  const boost = (t: CallTemplate) => (specialty && t.specialistBoost?.includes(specialty) ? 1 : 0);
  const sorted = [...TEMPLATES].sort((a, b) => boost(b) - boost(a));

  return (
    <div>
      <button className={styles.backLink} onClick={onBack}>
        <ArrowLeft size={13} /> Back to agents
      </button>
      <PageHeader
        title="New use case — choose a starting point"
        subtitle="Each template comes fully written. Pick one, then customize the script, knowledge, and routing. A tracked number is set up for you automatically. Emergency safety rules are locked on in every template."
      />
      <div className={styles.galleryGrid}>
        {sorted.map((t) => {
          const Icon = t.icon;
          const instances = instancesOf(t, agents);
          const atLimit = instances.length >= t.maxInstances;
          return (
            <Card key={t.id} className={styles.templateCard} padding="lg">
              <div className={`${styles.templateIcon} ${styles[t.iconBgClass]}`}>
                <Icon size={18} color={t.iconColor} />
              </div>
              <div className={styles.cardTitleRow}>
                <div className={styles.cardTitle}>{t.name}</div>
                {atLimit ? (
                  <Badge variant="success" size="sm">
                    <Check size={10} /> Active
                  </Badge>
                ) : (
                  templateBadge(t)
                )}
              </div>
              <p className={styles.cardBlurb}>{t.blurb}</p>
              <div className={styles.templateHandles}>
                {t.handles.map((h) => (
                  <Badge key={h} variant="neutral" size="sm">
                    {h}
                  </Badge>
                ))}
              </div>
              <div className={styles.templateBtnRow}>
                {atLimit ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    rightIcon={<ArrowRight size={13} />}
                    onClick={() => onOpenAgent(instances[0])}
                  >
                    Open your agent
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    rightIcon={<ArrowRight size={13} />}
                    onClick={() => onSelect(t.id)}
                  >
                    Use this template
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ── Agent list ────────────────────────────────────────────────────────────────

const HEALTH_BADGE: Record<AgentHealthIssue['severity'], 'error' | 'warning' | 'neutral'> = {
  error: 'error',
  warning: 'warning',
  info: 'neutral',
};

function AgentList({
  agents,
  issues,
  onOpen,
  onNew,
}: {
  agents: CallAgent[];
  issues: AgentHealthIssue[];
  onOpen: (a: CallAgent) => void;
  onNew: () => void;
}) {
  const canManage = useCanManageCalls();
  const problems = issues.filter((i) => i.severity !== 'info');
  if (!agents.length) {
    return (
      <>
        <PageHeader
          title="Agents"
          subtitle="An AI voice agent answers a tracked number, follows a script you control, and emails the front desk after every call."
        />
        <EmptyState
          icon={<PhoneCall size={22} />}
          title="Answer every call — even after hours"
          description="Pick a use case from the template gallery and a tracked number is provisioned with your approval. Calls are recorded and transcribed (callers are told up front), and your front desk gets a summary email."
          actions={
            <div className={styles.emptyActions}>
              <div className={styles.emptyTemplates}>
                {TEMPLATES.slice(0, 6).map((t) => {
                  const Icon = t.icon;
                  return (
                    <div key={t.id} className={styles.emptyTemplateCard}>
                      <div className={`${styles.emptyTemplateIcon} ${styles[t.iconBgClass]}`}>
                        <Icon size={15} color={t.iconColor} />
                      </div>
                      <div className={styles.emptyTemplateName}>{t.name}</div>
                      <div className={styles.emptyTemplateHandles}>{t.handles.join(' · ')}</div>
                    </div>
                  );
                })}
              </div>
              {canManage ? (
                <Button onClick={onNew} rightIcon={<ArrowRight size={14} />}>
                  Browse the template gallery
                </Button>
              ) : (
                <p className={styles.hintText}>
                  Ask a manager, admin or owner to set up a call agent.
                </p>
              )}
            </div>
          }
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Agents"
        subtitle="Call use cases you've set up. Each answers a tracked number, follows its script, and emails the front desk after every call. Browse all templates under New use case."
        actions={
          canManage ? (
            <Button onClick={onNew} leftIcon={<Plus size={14} />}>
              New use case
            </Button>
          ) : undefined
        }
      />
      {problems.length > 0 && (
        <div className={styles.healthBanner} role="alert">
          <div className={styles.healthBannerTitle}>
            <AlertTriangle size={14} aria-hidden /> {problems.length} agent issue
            {problems.length === 1 ? '' : 's'} need attention
          </div>
          <ul className={styles.healthList}>
            {problems.map((i, idx) => (
              <li key={`${i.agentId}-${idx}`}>
                <strong>{i.agentName}:</strong> {i.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className={styles.agentGrid}>
        {agents.map((a) => {
          const agentIssues = issues.filter((i) => i.agentId === a.id);
          const worst =
            agentIssues.find((i) => i.severity === 'error') ??
            agentIssues.find((i) => i.severity === 'warning') ??
            agentIssues[0];
          const t = resolveTemplate(a.templateKey);
          const Icon = t?.icon ?? PhoneCall;
          const iconColor = t?.iconColor ?? 'var(--accent)';
          const iconBgClass = t?.iconBgClass ? styles[t.iconBgClass] : styles.templateIconCustom;
          return (
            <div
              key={a.id}
              className={`${patterns.panel} ${styles.agentCard}`}
              {...activatableProps(() => onOpen(a), `Open ${a.name}`)}
            >
              <div className={styles.agentCardHeader}>
                <div className={patterns.cardLeft}>
                  <div className={`${styles.agentCardIcon} ${iconBgClass}`}>
                    <Icon size={16} color={iconColor} />
                  </div>
                  <div>
                    <div className={styles.cardTitle}>{a.name}</div>
                    <div className={styles.agentCardType}>{t?.name ?? 'Custom'}</div>
                  </div>
                </div>
                {agentStatusBadge(a.status)}
              </div>
              <div className={styles.agentCardMeta}>
                <span className={styles.metaRow}>
                  <Hash size={11} />{' '}
                  {a.trackingNumber
                    ? formatPhoneDisplay(a.trackingNumber)
                    : 'Number pending — provisioned when you activate'}
                </span>
                <span className={styles.metaRowAccent}>
                  Open builder <ArrowRight size={11} />
                </span>
              </div>
              {worst && (
                <div className={styles.agentCardIssue}>
                  <Badge variant={HEALTH_BADGE[worst.severity]} size="sm">
                    {worst.severity === 'info' ? 'Note' : 'Needs attention'}
                  </Badge>{' '}
                  {worst.message}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <ClosuresPanel />
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

type View = 'list' | 'pick' | 'build';

export function AgentsPage() {
  const { agents, locationId, isLoading, isError, refetch, isFetching } = useCallAgents();
  const actions = useCallAgentActions(locationId);
  const { currentLocation } = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState<View>('list');
  const [draft, setDraft] = useState<DraftAgent | null>(null);
  const { availableNumbers } = useCallAvailableNumbers();
  const { issues } = useAgentHealth();

  const { data: profileData } = useQuery({
    queryKey: settingsKeys.locationProfile(locationId),
    queryFn: () => locationsApi.getProfile(locationId),
    enabled: !!locationId,
  });
  const profile: LocationProfile | null = profileData?.data ?? null;

  const { data: practiceData } = useQuery({
    queryKey: settingsKeys.practice(),
    queryFn: () => settingsApi.getPractice(),
  });
  const specialty = practiceData?.data?.specialty ?? null;

  const { data: emergencyTopics } = useQuery({
    queryKey: callsKeys.templateTopics('emergency'),
    queryFn: () => callsApi.getTemplateTopics('emergency'),
    enabled: !!locationId,
  });

  const goList = () => {
    setView('list');
    setDraft(null);
  };
  const goNew = () => setView('pick');

  const handleSelectTemplate = (id: TemplateKey) => {
    // profile.languages is a free-text "languages spoken at this practice" list
    // (e.g. ["English","Spanish","Korean"]) — not Retell locale codes. Only use
    // it to decide whether Spanish should be pre-selected alongside English.
    const speaksSpanish = profile?.languages?.some((l) => /spanish|español/i.test(l)) ?? false;
    const prefill: Prefill = {
      hours: formatOfficeHours(profile?.officeHours ?? null),
      address: formatAddress(currentLocation),
      languages: speaksSpanish ? ['en-US', 'es-419'] : ['en-US'],
      areaCode: areaCodeFromPhone(currentLocation?.phone),
    };
    setDraft(makeDraft(id, prefill, emergencyTopics));
    setView('build');
  };

  const handleOpenAgent = (a: CallAgent) => navigate(a.id);

  const handleSave = () => {
    if (!draft) return;
    actions.create.mutate(draftToInput(draft, locationId), {
      onSuccess: (created) => navigate(created.id, { replace: true }),
    });
  };

  const handleActivate = (existingPhoneNumber?: string) => {
    if (!draft) return;
    // Activation runs on the detail page (see AgentDetailPage's autoActivate)
    // so it owns the spinner and refreshes the draft when Retell finishes —
    // otherwise the builder keeps offering "Activate" mid-flight.
    actions.create.mutate(draftToInput(draft, locationId), {
      onSuccess: (created) => {
        const state: AutoActivateState = { autoActivate: true, existingPhoneNumber };
        navigate(created.id, { replace: true, state });
      },
    });
  };

  if (isError) {
    return (
      <div className={patterns.page}>
        <PageHeader title="Agents" />
        <LoadError
          title="Couldn't load your agents"
          onRetry={() => refetch()}
          isRetrying={isFetching}
        />
      </div>
    );
  }

  if (!locationId || isLoading) {
    return (
      <div className={patterns.page}>
        <div className={styles.loadingSkeleton} />
      </div>
    );
  }

  return (
    <div className={patterns.page}>
      {view === 'list' && (
        <AgentList agents={agents} issues={issues} onOpen={handleOpenAgent} onNew={goNew} />
      )}
      {view === 'pick' && (
        <Gallery
          agents={agents}
          specialty={specialty}
          onSelect={handleSelectTemplate}
          onOpenAgent={handleOpenAgent}
          onBack={goList}
        />
      )}
      {view === 'build' && draft && (
        <AgentBuilder
          draft={draft}
          setDraft={(fn) => setDraft((prev) => (prev ? fn(prev) : prev))}
          onBack={goList}
          onSave={handleSave}
          onActivate={handleActivate}
          onPause={() => {}}
          onClone={() => {}}
          onDelete={() => {}}
          isSaving={actions.create.isPending}
          isActivating={actions.activate.isPending}
          isCloning={false}
          isDeleting={false}
          availableNumbers={availableNumbers}
        />
      )}
    </div>
  );
}
